import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import type { MegaColumn } from "@/lib/catalog";
import { getCategoryImage } from "@/lib/catalog";
import { cdnImage, IMG } from "@/lib/img";
import { useT } from "@/lib/i18n";
import { useFocusTrap } from "@/lib/useFocusTrap";
import { useCatalogOverrides } from "@/lib/catalog-overrides";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { GROUP_LABEL, RAIL, sectionsFor } from "./nav-data";
import { useNavSections } from "@/lib/tile-relevance";

/**
 * Nav-thumbnails die ALLEEN op mobiel afwijken. In het mobiele menu is de thumb klein en
 * vierkant (object-cover); een echte productfoto staat daar mooier dan de schematische
 * variant-tekening die de desktop-mega-nav toont. Desktop blijft dus ongewijzigd.
 */
const MOBIEL_NAV_IMG: Record<string, string> = {
  vacuumglas: "/glas-foto/vacuumglas-tegel.webp",
};

/**
 * Mobiel fullscreen drill-down-menu (Coolblue-stijl): woorden-lijsten met een klein
 * fotootje per hoofdcategorie. Drie niveaus: hoofdcategorieën → subcategorieën →
 * items. Elke stap heeft een terug-pijl; navigeren sluit het menu.
 */
export function MobileMenu({ onClose }: { onClose: () => void }) {
  const t = useT();
  // Nodig voor de nav-fototjes: uitgezette soorten/uitvoeringen mogen de tegelfoto niet bepalen.
  const catalogOv = useCatalogOverrides();
  const trapRef = useFocusTrap<HTMLDivElement>();
  // Drill-down-stack: [] = hoofdlijst, [slug] = categorie, [slug, sectionIdx] = subcategorie.
  const [catSlug, setCatSlug] = useState<string | null>(null);
  const [sectionIdx, setSectionIdx] = useState<number | null>(null);

  // Body-scroll locken zolang het menu open is (zelfde patroon als DrawingModal).
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const cat = catSlug ? RAIL.find((r) => r.slug === catSlug) ?? null : null;
  // Zelfde relevantie-regel als de desktop-nav en de categorietegels.
  const sections: MegaColumn[] = useNavSections(catSlug);
  const section = sectionIdx != null ? sections[sectionIdx] ?? null : null;

  const back = (): void => {
    if (sectionIdx != null) setSectionIdx(null);
    else setCatSlug(null);
  };

  const rowCls =
    "flex min-h-12 w-full items-center gap-3 px-4 text-left text-base font-medium text-primary-800 transition active:bg-primary-50";

  const soonBadge = (
    <span className="shrink-0 rounded-full bg-primary-50 px-2 py-0.5 text-[10px] font-semibold uppercase text-primary-700">{t("Binnenkort")}</span>
  );

  // Via een portal naar body: anders zit het menu in de header-stacking-context (z-30) en
  // valt de sticky afrekenbalk van de winkelwagen (z-40) er op mobiel overheen.
  return createPortal(
    <div className="fixed inset-0 z-50 lg:hidden">
      {/* Dim-overlay — de site blijft er net zichtbaar achter (Coolblue-stijl); tik = sluiten */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden />
      <div ref={trapRef} tabIndex={-1} className="absolute inset-y-0 left-0 flex w-[86%] max-w-[360px] flex-col bg-white shadow-2xl" role="dialog" aria-modal="true" aria-label={t("Menu")}>
      {/* Menu-header: terug-pijl (dieper niveau) · titel · sluiten */}
      <div className="flex h-14 shrink-0 items-center gap-1 border-b border-primary-100 px-2">
        {(catSlug || sectionIdx != null) ? (
          <button type="button" onClick={back} aria-label={t("Terug")} className="inline-flex h-11 w-11 items-center justify-center text-primary-700">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 6l-6 6 6 6" /></svg>
          </button>
        ) : (
          <span className="w-2" aria-hidden />
        )}
        <p className="flex-1 truncate text-base font-bold text-primary-900">
          {section ? t(section.title ?? "") : cat ? t(cat.label) : t("Menu")}
        </p>
        <button type="button" onClick={onClose} aria-label={t("Sluiten")} className="inline-flex h-11 w-11 items-center justify-center text-primary-700">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {/* Niveau 2 — items van een subcategorie */}
        {section ? (
          <ul className="divide-y divide-primary-100">
            <li>
              <Link to={section.titleTo ?? cat?.to ?? "/"} onClick={onClose} className={`${rowCls} font-semibold text-accent-700`}>
                {t("Alles in")} {t(section.title ?? "")} →
              </Link>
            </li>
            {section.links.map((l) => (
              <li key={l.label}>
                <Link to={l.to} onClick={onClose} className={`${rowCls} ${l.soon ? "text-primary-600" : ""}`}>
                  <span className="flex-1">{t(l.label)}</span>
                  {l.soon && soonBadge}
                </Link>
              </li>
            ))}
          </ul>
        ) : cat ? (
          /* Niveau 1 — subcategorieën van een hoofdcategorie */
          <ul className="divide-y divide-primary-100">
            <li>
              <Link to={cat.to} onClick={onClose} className={`${rowCls} font-semibold text-accent-700`}>
                {t("Alles in")} {t(cat.label)} →
              </Link>
            </li>
            {sections.map((s, i) =>
              s.links.length > 0 ? (
                <li key={s.title ?? i}>
                  <button type="button" onClick={() => setSectionIdx(i)} className={rowCls}>
                    <span className="flex-1">{t(s.title ?? "")}</span>
                    {s.soon ? soonBadge : <span aria-hidden className="text-primary-300">›</span>}
                  </button>
                </li>
              ) : (
                <li key={s.title ?? i}>
                  <Link to={s.titleTo ?? cat.to} onClick={onClose} className={`${rowCls} ${s.soon ? "text-primary-600" : ""}`}>
                    <span className="flex-1">{t(s.title ?? "")}</span>
                    {s.soon && soonBadge}
                  </Link>
                </li>
              ),
            )}
          </ul>
        ) : (
          /* Niveau 0 — hoofdcategorieën (2 groepen) + service */
          <>
            {(["glas", "beslag"] as const).map((g) => (
              <div key={g}>
                <p className="px-4 pb-1 pt-4 text-[11px] font-semibold uppercase tracking-wider text-primary-600">{t(GROUP_LABEL[g])}</p>
                <ul className="divide-y divide-primary-100 border-y border-primary-100">
                  {RAIL.filter((r) => r.group === g).map((r) => {
                    // Categoriefoto; glas-categorieën hebben geen artikelen → val terug op
                    // de eerste subcategorie-foto uit de panes. MOBIEL_NAV_IMG wint: op mobiel
                    // staat een echte productfoto mooier dan de schematische variant-tekening.
                    const img = MOBIEL_NAV_IMG[r.slug] ?? getCategoryImage(r.slug, catalogOv) ?? sectionsFor(r.slug).find((s) => s.image)?.image;
                    return (
                      <li key={r.slug}>
                        <button type="button" onClick={() => setCatSlug(r.slug)} className={rowCls}>
                          <span className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg">
                            {img && <img src={cdnImage(img, { w: IMG.thumb })} alt="" loading="lazy" decoding="async" width={48} height={48} className="h-full w-full object-cover" />}
                          </span>
                          <span className="flex-1">{t(r.label)}</span>
                          <span aria-hidden className="text-primary-300">›</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}

            {/* Service-links (woorden) + taal */}
            <ul className="mt-4 divide-y divide-primary-100 border-y border-primary-100">
              {([
                { label: "Acties", to: "/acties" },
                { label: "Kennisbank", to: "/kennisbank" },
                { label: "Zakelijk", to: "/zakelijk" },
                { label: "Levertijden & verzending", to: "/levertijden" },
                { label: "Contact", to: "/contact" },
              ] as const).map((l) => (
                <li key={l.to}>
                  <Link to={l.to} onClick={onClose} className={rowCls}>{t(l.label)}</Link>
                </li>
              ))}
            </ul>

            <div className="flex items-center justify-between gap-3 px-4 py-4">
              <Link to="/glassoorten" onClick={onClose} className="inline-flex min-h-11 flex-1 items-center justify-center rounded-lg bg-accent-700 px-5 font-semibold text-white transition hover:bg-accent-800">
                {t("Glas samenstellen")} →
              </Link>
              <LanguageSwitcher tone="header" className="shrink-0" />
            </div>
          </>
        )}
      </div>
      </div>
    </div>,
    document.body,
  );
}
